import { For, Show, createEffect, createSignal, createUniqueId, on, onCleanup, onMount } from 'solid-js';
import clsx from 'clsx';

export type TabnavItem = {
    key: string;
    label: string;
    disabled?: boolean;
};

type TabnavProps = {
    tabs: TabnavItem[];
    activeKey: string;
    onChange: (key: string) => void;
    ariaLabel?: string;
    overflowAriaLabel?: string;
};

const tabClasses = (active: boolean, disabled: boolean) => clsx(
    'shrink-0 label-md font-cvi-medium bg-transparent border-0 cursor-pointer whitespace-nowrap',
    'py-cvi-5 border-b-2 transition-colors duration-200',
    disabled
        ? 'text-cvi-fg-default opacity-40 cursor-not-allowed border-transparent'
        : active
            ? 'text-cvi-fg-default border-cvi-border-interactive'
            : 'text-cvi-fg-default border-transparent',
);

export const Tabnav = (props: TabnavProps) => {
    let containerRef: HTMLDivElement | undefined;
    let rowRef: HTMLDivElement | undefined;
    let overflowBtnRef: HTMLButtonElement | undefined;
    let overflowTriggerRef: HTMLButtonElement | undefined;
    // Slow-leak: entries are added on tab mount but never pruned when a tab is
    // removed from `props.tabs`. Fine for stable lists (the bookmarks page has
    // 6 fixed category keys); revisit if a consumer churns tabs at scale.
    const tabRefs = new Map<string, HTMLButtonElement>();

    const [ windowStart, setWindowStart ] = createSignal(0);
    const [ windowEnd, setWindowEnd ] = createSignal(props.tabs.length);
    const [ translateX, setTranslateX ] = createSignal(0);
    const [ clipWidth, setClipWidth ] = createSignal<number | null>(null);
    const [ menuOpen, setMenuOpen ] = createSignal(false);
    const [ animate, setAnimate ] = createSignal(false);

    // SSR-safe unique id so the overflow trigger can `aria-controls` the menu container.
    const menuId = createUniqueId();

    let rafToken: number | undefined;
    const scheduleRecompute = () => {
        if (rafToken !== undefined) return;
        rafToken = requestAnimationFrame(() => {
            rafToken = undefined;
            recompute();
        });
    };

    // Decide which tabs to show, where the row sits, and how wide the clip is.
    // Algorithm:
    //   1. Measure every tab's natural width from its rendered DOM ref.
    //   2. If the whole row fits in the container, no overflow — render all.
    //   3. Otherwise, build a contiguous window around the active tab,
    //      expanding outward (left first, then right) one neighbour at a time
    //      until the next neighbour wouldn't fit alongside the more button.
    //   4. Translate the row so tab[start] sits flush with the clip's left
    //      edge, and size the clip to exactly the windowed content's width
    //      so tabs outside the window can't bleed into the visible area.
    const recompute = () => {
        if (!containerRef || !rowRef) return;
        const tabs = props.tabs;
        const n = tabs.length;
        if (n === 0) {
            setWindowStart(0);
            setWindowEnd(0);
            setTranslateX(0);
            setClipWidth(0);
            return;
        }

        const widths = tabs.map((t) => tabRefs.get(t.key)?.offsetWidth ?? 0);
        // A 0-width measurement means a tab hasn't laid out yet (e.g. fonts
        // still loading, or this fired before the first paint completed).
        // Bail and let the next rAF or ResizeObserver tick re-measure.
        if (widths.some((w) => w === 0)) return;

        const gap = parseFloat(getComputedStyle(rowRef).columnGap) || 0;
        const overflowW = overflowBtnRef?.offsetWidth ?? 0;
        const containerGap = parseFloat(getComputedStyle(containerRef).columnGap) || 0;
        // Width available for tabs when no overflow is needed (no more button).
        const fullW = containerRef.clientWidth;
        // Width available for tabs when there will be overflow (reserve more button + gap).
        const constrainedW = fullW - overflowW - containerGap;

        const totalWidth = widths.reduce((a, b) => a + b, 0) + gap * (n - 1);

        // Everything fits even without reserving space for the more button.
        if (totalWidth <= fullW + 0.5) {
            setWindowStart(0);
            setWindowEnd(n);
            setTranslateX(0);
            setClipWidth(totalWidth);
            return;
        }

        // It doesn't all fit — we'll show the more button. Window what fits in
        // `constrainedW` (which already reserves the more button + gap).
        const limit = constrainedW;

        const activeIdx = Math.max(0, tabs.findIndex((t) => t.key === props.activeKey));
        let start = activeIdx;
        let end = activeIdx + 1;
        let used = widths[activeIdx];

        // Expand outward, preferring left so the active tab sits toward the
        // right of the visible window — matches the reference design.
        while (start > 0 || end < n) {
            let expanded = false;
            if (start > 0) {
                const next = used + gap + widths[start - 1];
                if (next <= limit) {
                    used = next;
                    start -= 1;
                    expanded = true;
                }
            }
            if (end < n) {
                const next = used + gap + widths[end];
                if (next <= limit) {
                    used = next;
                    end += 1;
                    expanded = true;
                }
            }
            if (!expanded) break;
        }

        // Defensive: guarantee at least one visible tab even at degenerate
        // widths where the active tab alone wouldn't fit. Better to clip one
        // tab on its right edge than render an empty row.
        if (end === start) end = start + 1;

        setWindowStart(start);
        setWindowEnd(end);

        // Slide the row so tab[start] sits flush with the clip's left edge.
        let offset = 0;
        for (let i = 0; i < start; i++) offset += widths[i] + gap;
        setTranslateX(-offset);

        // Clip width = width occupied by the windowed tabs only, so tabs
        // outside the window don't bleed into the visible area.
        let windowed = 0;
        for (let i = start; i < end; i++) {
            windowed += widths[i];
            if (i > start) windowed += gap;
        }
        setClipWidth(windowed);
    };

    onMount(() => {
        // Two rAFs: the first lets layout settle so we can measure, the second
        // ensures the initial transform/clipWidth values have been committed
        // *before* enabling transitions. Without the second hop, the very first
        // frame would animate from `translateX(0)` to whatever recompute set —
        // visible as a jarring slide on page load.
        requestAnimationFrame(() => {
            recompute();
            requestAnimationFrame(() => setAnimate(true));
        });

        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined' && containerRef) {
            ro = new ResizeObserver(() => scheduleRecompute());
            ro.observe(containerRef);
        }

        const onDocClick = (e: MouseEvent) => {
            if (!menuOpen()) return;
            if (containerRef && !containerRef.contains(e.target as Node)) setMenuOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && menuOpen()) {
                setMenuOpen(false);
                overflowTriggerRef?.focus();
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKeyDown);

        onCleanup(() => {
            if (rafToken !== undefined) cancelAnimationFrame(rafToken);
            ro?.disconnect();
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKeyDown);
        });
    });

    // Re-measure when the tab list or the active tab changes.
    createEffect(on(
        () => [ props.tabs.map((t) => t.key).join('|'), props.activeKey ] as const,
        () => scheduleRecompute(),
        { defer: true },
    ));

    const isHidden = (idx: number) => idx < windowStart() || idx >= windowEnd();

    const overflowTabs = () => props.tabs.filter((_t, i) => isHidden(i));
    const hasOverflow = () => overflowTabs().length > 0;

    const handleSelect = (item: TabnavItem) => {
        if (item.disabled) return;
        props.onChange(item.key);
        if (menuOpen()) {
            setMenuOpen(false);
            overflowTriggerRef?.focus();
        }
    };

    return (
        <div ref={containerRef} class="relative flex items-center w-full gap-cvi-8">
            <div
                class={clsx(
                    'min-w-0 overflow-x-clip overflow-y-visible box-content px-cvi-8 -mx-cvi-8',
                    animate() && 'transition-[width] duration-300 ease-out',
                )}
                style={clipWidth() !== null ? { width: `${clipWidth()}px` } : { flex: '1 1 0%' }}
            >
                <div
                    ref={rowRef}
                    role="tablist"
                    aria-label={props.ariaLabel}
                    class={clsx(
                        'flex items-center gap-cvi-8 w-max',
                        animate() && 'transition-transform duration-300 ease-out',
                    )}
                    style={{ transform: `translateX(${translateX()}px)` }}
                >
                    <For each={props.tabs}>
                        {(tab, i) => {
                            const hidden = () => isHidden(i());
                            return (
                                <button
                                    ref={(el) => tabRefs.set(tab.key, el)}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab.key === props.activeKey}
                                    aria-hidden={hidden()}
                                    tabIndex={hidden() ? -1 : 0}
                                    disabled={tab.disabled}
                                    class={tabClasses(tab.key === props.activeKey, !!tab.disabled)}
                                    onClick={() => handleSelect(tab)}
                                >
                                    {tab.label}
                                </button>
                            );
                        }}
                    </For>
                </div>
            </div>

            <Show when={hasOverflow()}>
                <button
                    ref={overflowTriggerRef}
                    type="button"
                    aria-label={props.overflowAriaLabel ?? 'More'}
                    aria-haspopup="true"
                    aria-expanded={menuOpen()}
                    aria-controls={menuId}
                    onClick={() => setMenuOpen((o) => !o)}
                    class="icon-button-ghost icon-button-ghost-md shrink-0"
                >
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="4" cy="10" r="1.5" fill="currentColor" />
                        <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                        <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                    </svg>
                </button>
            </Show>
            {/* Sizing twin: invisible but laid out so recompute() can read the
                more button's rendered width even when the real button isn't in
                the tree. We need this width to know whether the next tab would
                still fit if we *did* show the more button — chicken-and-egg
                without something measurable. `visibility: hidden` (not
                `display: none`) preserves the box dimensions; `position: absolute`
                keeps it out of the flex row's layout calculations.
                Alternatives considered: hard-code the width (fragile against
                token changes), expose the size as a CSS custom property and
                read it from `:root` (would couple us to a stylesheet
                convention not used elsewhere in this codebase), or briefly
                toggle the real button's display during measurement (causes a
                visible flash). The twin is the least-bad option. */}
            <button
                ref={overflowBtnRef}
                aria-hidden="true"
                tabIndex={-1}
                class="icon-button-ghost icon-button-ghost-md absolute invisible pointer-events-none"
            >
                <svg viewBox="0 0 20 20" aria-hidden="true" />
            </button>

            <Show when={menuOpen() && hasOverflow()}>
                <div
                    id={menuId}
                    class={clsx(
                        'absolute right-0 top-full mt-cvi-4 z-10 w-cvi-overflow-menu',
                        'flex flex-col items-start py-cvi-4',
                        'bg-cvi-bg-elevation-raised border border-cvi-border-muted rounded-cvi-md',
                        'shadow-elevation-overlay',
                    )}
                >
                    <For each={overflowTabs()}>
                        {(tab) => (
                            <button
                                type="button"
                                disabled={tab.disabled}
                                title={tab.label}
                                class={clsx(
                                    'w-full text-left cursor-pointer border-0 bg-transparent truncate',
                                    'px-cvi-8 py-cvi-5 label-md text-cvi-fg-default',
                                    'hover:bg-cvi-bg-menu-hover',
                                    tab.disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
                                    tab.key === props.activeKey ? 'font-cvi-medium' : 'font-cvi-regular',
                                )}
                                onClick={() => handleSelect(tab)}
                            >
                                {tab.label}
                            </button>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
};
